import React from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import { buckets, packageStates, useTransportV2 } from "../context/TransportV2Context";
import { useLang } from "../i18n";
import PackagePrechecks from "./PackagePrechecks";
import { isPackageTransport, packageDesignObjects } from "../utils/packageTransport";

// Render server-resolved names, never reconstruct an ID from a source display name.
export function targetNameGroups(draft) {
  if (!["done", "partial"].includes(draft.precheckState)) return null;
  const result = [];
  for (const plant of buckets(draft)) {
    const entry = draft.precheckByBucket?.[`${draft.space?.qasInstance?.id}__${plant.kennung || "std"}`];
    // Display metadata travels separately from the technical package identity.
    const packageName = entry?.targetPackageName || (!plant.kennung ? draft.pkgName : "");
    if (!entry || entry.error || !entry.targetPackageId || !packageName || !Array.isArray(entry.comparison)
      || entry.comparison.some(row => !row.suffixedId || !row.targetName)) return null;
    // Pakettransport: Die Designobjekte reisen mit und erscheinen mit ihrem Zielnamen (nur Servernamen).
    const key = plant.kennung || "std";
    const design = isPackageTransport(draft) ? packageDesignObjects({ precheckByBucket: { [key]: entry } })
      .map(object => ({ ...object, target: object.byWerk[key] || {} })) : [];
    result.push({ plant, entry, packageName, design });
  }
  return result;
}

export default function TargetNamesDialog({ onClose, onContinue }) {
  const { state, dispatch } = useTransportV2();
  const { t } = useLang();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const drafts = packageStates(state);
  const previews = drafts.map(draft => ({ draft, groups: targetNameGroups(draft) }));
  const ready = previews.length > 0 && previews.every(p => p.groups !== null);
  const retry = draft => dispatch({ type: "PACKAGE_PRECHECK_REQUEST", pkgId: draft.pkgId, generation: state.precheckGeneration });
  const buttonSx = { minHeight: 44, textTransform: "none", fontFamily: "inherit", color: "var(--iio-text-1)",
    "&.Mui-focusVisible": { outline: "2px solid var(--iio-signal)", outlineOffset: 2 } };
  return <Dialog open onClose={onClose} maxWidth="lg" fullWidth scroll="paper" aria-labelledby="target-names-title"
    transitionDuration={reducedMotion ? 0 : undefined}
    PaperProps={{ className: "iio", sx: { m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
      maxHeight: "calc(100dvh - 32px)", border: "1px solid var(--iio-border)", borderRadius: "12px",
      fontFamily: '"Source Sans 3", sans-serif', color: "var(--iio-text-1)", background: "var(--iio-bg-1)", backgroundImage: "none" } }}>
    <PackagePrechecks preview />
    <DialogTitle id="target-names-title" sx={{ px: { xs: 2, sm: 3 }, py: 2, fontFamily: "inherit", fontWeight: 600 }}>
      {t("preview.title")}
      <Typography component="div" sx={{ fontFamily: "inherit", fontSize: 14, color: "var(--iio-text-3)", overflowWrap: "anywhere" }}>
        {state.space?.qasInstance?.name || state.space?.qasInstance?.id}
      </Typography>
    </DialogTitle>
    <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 }, borderColor: "var(--iio-border)", overflowX: "hidden" }}>
      {previews.map(({ draft, groups }) => {
        const failed = draft.precheckState === "error" || (["done", "partial"].includes(draft.precheckState) && !groups);
        if (!groups) return <Box key={draft.pkgId} sx={{ py: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          {!failed && <CircularProgress size={22} aria-label={t("preview.loading")} sx={{ color: "var(--iio-signal)",
            ...(reducedMotion && { animation: "none", "& circle": { animation: "none" } }) }} />}
          <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
            <Typography sx={{ fontFamily: "inherit", fontWeight: 600 }}>{draft.pkgName || draft.pkgId}</Typography>
            {draft.pkgName && draft.pkgName !== draft.pkgId && <Typography sx={{ fontSize: 13 }}>{draft.pkgId}</Typography>}
            <Typography role={failed ? "alert" : "status"} sx={{ fontFamily: "inherit", fontSize: 14, color: failed ? "var(--iio-critical)" : "var(--iio-text-3)" }}>
              {failed ? typeof draft.precheckError === "string" && draft.precheckError !== "precheck fehlgeschlagen"
                ? draft.precheckError : t("preview.failed") : t("preview.loading")}
            </Typography>
          </Box>
          {failed && <Button onClick={() => retry(draft)} sx={buttonSx}>{t("err.retry")}</Button>}
        </Box>;
        return groups.map(({ plant, entry, packageName, design }) => <Box key={`${draft.pkgId}:${plant.key}`} sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
          {plant.kennung && <Typography component="h3" sx={{ mt: 0, mb: 1, fontSize: 15, fontFamily: "inherit", fontWeight: 600 }}>
            {plant.name} · {plant.kennung}
          </Typography>}
          <Table size="small" aria-label={`${entry.targetPackageId}${plant.kennung ? ` · ${plant.kennung}` : ""}`}
            sx={{ tableLayout: "fixed", "& .MuiTableCell-root": { color: "var(--iio-text-1)", fontFamily: "inherit", borderColor: "var(--iio-border)",
              fontSize: { xs: 13, sm: 14 }, px: { xs: 1, sm: 1.5 }, py: 1.5, verticalAlign: "top", overflowWrap: "anywhere" },
            "& .MuiTableCell-head": { fontWeight: 600, color: "var(--iio-text-3)" } }}>
            <TableHead><TableRow>
              <TableCell sx={{ width: { xs: "22%", sm: "15%" } }}>{t("preview.object")}</TableCell>
              <TableCell>{t("preview.name")}</TableCell><TableCell>{t("preview.id")}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              <TableRow sx={{ background: "var(--iio-bg-2)", "& td": { fontWeight: 600 } }}>
                <TableCell component="th" scope="row">Package</TableCell><TableCell>{packageName}</TableCell><TableCell>{entry.targetPackageId}</TableCell>
              </TableRow>
              {entry.comparison.map(row => <TableRow key={row.originalId || row.Id}>
                <TableCell component="th" scope="row">iFlow{row.draft && <Box component="span" sx={{ display: "block", color: "var(--iio-text-3)", fontSize: 12 }}>{t("st.draft")}</Box>}</TableCell>
                <TableCell>{row.targetName}</TableCell>
                <TableCell>{row.suffixedId}
                  {/* DEV181: Nur diese Zeile ist betroffen; der Servertext nennt Artefakt, Ziel-ID, Paket und Abhilfe. */}
                  {row.conflict?.message && <Box component="span" role="alert" sx={{ display: "block", mt: 0.5, fontSize: 13, color: "var(--iio-critical)" }}>
                    {row.conflict.message}</Box>}
                </TableCell>
              </TableRow>)}
              {design.map(object => <TableRow key={`design:${object.id}`}>
                <TableCell component="th" scope="row">{object.type}</TableCell>
                <TableCell>{object.target.targetName || <Box component="span" sx={{ color: "var(--iio-text-3)" }}>{t("pkgT.renamed")}</Box>}</TableCell>
                <TableCell>{object.target.targetId || ""}</TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </Box>);
      })}
    </DialogContent>
    <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, flexWrap: "wrap" }}>
      <Button autoFocus onClick={onClose} sx={buttonSx}>{t("common.back")}</Button>
      <Button variant="contained" disableElevation disabled={!ready} onClick={() => ready && onContinue()}
        sx={{ ...buttonSx, backgroundColor: "var(--iio-signal)", color: "var(--iio-hero-text)",
          "&:hover": { backgroundColor: "var(--iio-signal-2)" } }}>{t("s2.next")}</Button>
    </DialogActions>
  </Dialog>;
}
